// Toast viewport — renders the stack of active toasts in the top-right.
// Mount once at the app root (inside AppLayout). Each toast is keyed by
// its store id so React stably manages enter/exit transitions.
//
// To trigger a toast from anywhere:
//   import { toast } from "@/stores/toastStore";
//   toast.success("Saved");
//   toast.error("Failed: " + msg);
//   toast.info("Heads up...");
import { useDict } from "@/i18n/LocaleProvider";

import { useToastStore, type Toast as ToastT } from "@/stores/toastStore";

const VARIANT_STYLES: Record<
  ToastT["variant"],
  { fg: string; bg: string; border: string; icon: string }
> = {
  success: { fg: "rgb(var(--color-success))", bg: "rgb(var(--color-success-bg))", border: "rgb(var(--color-success) / 0.333)", icon: "✓" },
  error:   { fg: "rgb(var(--color-danger))", bg: "rgb(var(--color-danger-bg))", border: "rgb(var(--color-danger) / 0.333)", icon: "✕" },
  info:    { fg: "rgb(var(--color-accent))", bg: "rgb(var(--color-accent-bg))", border: "rgb(var(--color-accent) / 0.333)", icon: "ℹ" },
};

export function ToastViewport() {
  const ui = useDict().app.ui;
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  if (items.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: "min(420px, calc(100vw - 32px))" }}
      aria-live="polite"
      aria-atomic="false"
    >
      {items.map((t) => {
        const v = VARIANT_STYLES[t.variant];
        return (
          <div
            key={t.id}
            className="rounded pointer-events-auto animate-fade-in flex items-start gap-3 px-3 py-2"
            style={{
              background: v.bg,
              border: `1px solid ${v.border}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}
            role={t.variant === "error" ? "alert" : "status"}
          >
            <div
              className="font-mono text-[14px] font-bold leading-none mt-[2px]"
              style={{ color: v.fg }}
              aria-hidden="true"
            >
              {v.icon}
            </div>
            <div
              className="font-sans text-[13px] flex-1 leading-[1.4] break-words"
              style={{ color: "rgb(var(--color-content))" }}
            >
              {t.message}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label={ui.dismiss}
              className="font-mono text-[14px] leading-none px-1 cursor-pointer bg-transparent border-none"
              style={{ color: "rgb(var(--color-muted))" }}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}
